import { useFormikContext } from 'formik';
import { FC, useMemo, useState } from 'react';
import _ from 'lodash';

import { useTranslation } from 'react-i18next';
import { DocumentInputUploadModal } from '../risk/DocumentInputUploadModal';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsFormHelperText } from '../common/basic/QcsFormHelperText';
import { BASE_URL_WITH_VERSION, mediaApi } from '../../utils/api';
import { getMediaToken } from '../../utils/storage';
import { IconButton, Tooltip, TypographyProps, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { QcsFileDropZone } from '../common/form/QcsFileDropZone';
import { QcsFileItem, QcsFileList } from '../common/form/QcsFileList';

import { QcsStack } from '../common/basic/QcsStack';
import { FileUploadMediaResponse } from '@api/models/invitation/FileUploadMediaResponse';

interface Props {
  titleVariant: TypographyProps['variant'];
  required?: boolean;
  disabled?: boolean;
}

export const InvitationAttachments: FC<Props> = ({ titleVariant, required, disabled }) => {
  const { values, touched, errors, submitCount, setFieldValue } = useFormikContext<any>();
  const { t } = useTranslation();
  const theme = useTheme();
  const [fileQueue, setFileQueue] = useState<File[]>([]);

  const currentFile = fileQueue.length > 0 ? fileQueue[0] : null;

  const mediaToken = getMediaToken();
  const attachments = _.get(values, 'attachments');
  const wasTouched = !!submitCount || !!_.get(touched, 'attachments');
  const errorText = _.get(errors, 'attachments') as string;
  const hasError = wasTouched && !!errorText;

  /* -- handlers ---------------------------------------------------- */

  const handleFiles = (files: File[]) => {
    setFileQueue((prev) => [...prev, ...files]);
  };

  const handleFileUploaded = async (document: FileUploadMediaResponse) => {
    if (!currentFile) return;
    setFieldValue('attachments', attachments ? [...attachments, document] : [document]);
    setFileQueue((prev) => prev.slice(1));
  };

  const handleFileCancel = () => {
    setFileQueue((prev) => prev.slice(1));
  };

  const handleFileRemove = (item: QcsFileItem) => {
    void mediaApi.deleteFile(item.id);
    setFieldValue(
      'attachments',
      attachments.filter((x: FileUploadMediaResponse) => x.id !== item.id),
    );
  };

  const fileItems = useMemo<QcsFileItem[]>(
    () =>
      attachments?.map((x: FileUploadMediaResponse) => ({
        id: x.id!,
        fileName: x.fileName!,
        documentUrl: `${BASE_URL_WITH_VERSION}${x.path}?token=${mediaToken}`,
      })) ?? [],
    [attachments, mediaToken],
  );

  return (
    <QcsStack>
      <QcsStack direction="row" gap="0.25rem">
        <QcsTypography
          variant={titleVariant}
          sx={{
            color: hasError ? theme.palette.error.main : undefined,
          }}
        >
          {t('invitation.attachments')}
          {required && ' *'}
        </QcsTypography>
        <Tooltip title={t('invitation.attachmentsTooltip')} placement="right" slotProps={{ tooltip: { sx: { fontSize: '0.85rem' } } }}>
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </QcsStack>

      <DocumentInputUploadModal file={currentFile} onFileUploaded={handleFileUploaded} onCloseOnError={handleFileCancel} />

      <QcsFileList items={fileItems} onRemove={handleFileRemove} disabled={disabled} />

      <QcsFileDropZone onFiles={handleFiles} disabled={disabled} hasError={hasError} />

      {hasError && <QcsFormHelperText error>{errorText}</QcsFormHelperText>}
    </QcsStack>
  );
};
